import { styled, Typography } from '@mui/material';

export type CounterStyleType =
  | 'error'
  | 'warning'
  | 'primarySoft'
  | 'attentionSoft'
  | 'attentionOutlined';
export type CounterSize = 'sm' | 'md';
export type CounterShape = 'default' | 'circle';

export interface CounterProps {
  inverse?: boolean;
  disabled?: boolean;
  button?: boolean;
  styleType?: CounterStyleType;
  shape?: CounterShape;
  size?: CounterSize;
}

// Should be refactored when migrating to MUI v6+:
// MUI v6 solves it via the variants[] API as the canonical styling model.
// Each design state becomes a declarative { props: { color, appearance, size }, style } entry instead of conditional spreads.
export const Counter = styled(Typography, {
  shouldForwardProp: (prop) =>
    !(['inverse', 'disabled', 'button', 'styleType', 'size'] as PropertyKey[]).includes(prop),
})<CounterProps>(({ theme, inverse, disabled, button, styleType, size, shape = 'default' }) => {
  const hasBorder = disabled || inverse;

  return {
    flex: '0 0 auto',
    padding: theme.spacing(0.125, 0.5),
    marginLeft: theme.spacing(),
    borderRadius: Number.parseInt(theme.spacing(1.5)),
    border: hasBorder ? theme.mixins.borderValue() : undefined,
    backgroundColor: disabled ? theme.palette.common.white : theme.palette.primary.main,
    color: disabled ? theme.palette.text.secondary : theme.palette.common.white,

    ...(inverse && {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.common.white,
      color: theme.palette.primary.main,
    }),

    ...(button && {
      backgroundColor: theme.palette.common.greenOld,
      color: theme.palette.primary.main,

      '&&': {
        ...theme.typography.body3,
        lineHeight: theme.spacing(2.25),
        height: theme.spacing(2.25),
      },
    }),

    ...(styleType === 'error' && {
      borderColor: inverse ? theme.palette.error.main : undefined,
      color: inverse ? theme.palette.error.main : theme.palette.common.white,
      backgroundColor: inverse ? theme.palette.error.superLight : theme.palette.error.main,
    }),

    ...(styleType === 'warning' && {
      borderColor: inverse ? theme.palette.common.orange : undefined,
      color: inverse ? theme.palette.common.orange : theme.palette.common.white,
      backgroundColor: inverse ? theme.palette.common.orange5 : theme.palette.common.orange,
    }),

    ...(styleType === 'primarySoft' && {
      border: 'none',
      backgroundColor: theme.palette.common.main5,
      color: theme.palette.primary.main,
    }),

    ...(styleType === 'attentionSoft' && {
      border: 'none',
      backgroundColor: theme.palette.common.orange4,
      color: theme.palette.common.dijon1,
    }),

    ...(styleType === 'attentionOutlined' && {
      border: `1px solid ${theme.palette.common.orange4}`,
      backgroundColor: theme.palette.common.orange5,
      color: theme.palette.common.orange,
    }),

    ...(shape === 'circle' && {
      minWidth: theme.spacing(2.5),
      padding: theme.spacing(0, 0.5),
      textAlign: 'center',
    }),
    ...(size === 'sm' && {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 22,
      minHeight: 22,
      textAlign: 'center',
      lineHeight: 1,
      fontWeight: theme.typography.fontWeightSemiBold,
    }),
  };
});

Counter.defaultProps = {
  variant: 'body1',
  noWrap: true,
  // @ts-expect-error the `defaultProps` does not expose the `component` prop by any reason
  component: 'span',
};
